import * as styles from "./PeopleGroup.css";

import type { JSX } from "react";

import { Avatar } from "@frontend/components/atoms/Avatar";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { useLingui } from "@lingui/react/macro";

export type PersonCardProps = {
  name: string | null;
  hint: string | null;
  role: string | null;
  avatarUrl?: string | null;
  onEdit: (origin: DOMRect) => void;
  onRemove: () => void;
};

export function PersonCard({
  name,
  hint,
  role,
  avatarUrl,
  onEdit,
  onRemove,
}: PersonCardProps): JSX.Element {
  const { t } = useLingui();

  const label = name ?? t`Unknown person`;

  return (
    <div className={styles.card}>
      <button
        type="button"
        className={styles.cardBody}
        aria-label={t`Edit ${label}`}
        onClick={(event) => onEdit(event.currentTarget.getBoundingClientRect())}
      >
        <Avatar
          size="sm"
          url={avatarUrl ?? undefined}
          fallback={initials(label)}
        />

        <span className={styles.identity}>
          <span className={styles.name}>{label}</span>

          <span className={styles.meta}>
            {[role, hint].filter(Boolean).join(" · ")}
          </span>
        </span>
      </button>

      <Button
        variant="ghost"
        size="sm"
        className={styles.remove}
        aria-label={t`Remove`}
        leftSection={<Icon name="X" size="sm" />}
        onClick={onRemove}
      />
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join("");
}
